import { useControllableValue } from 'ahooks';
import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { error as errorConsoleLog } from '../_util/devConsoleLog';
import { pickDomAttrs } from '../_util/utils';
import Chips from '../chips';
import TextField from '../text-field';
import { jsx as _jsx } from "react/jsx-runtime";
function triggerFocus(element, option) {
  if (!element) return;
  element.focus(option);

  // Selection content
  const {
    cursor
  } = option || {};
  if (cursor) {
    const len = element.value.length;
    switch (cursor) {
      case 'start':
        element.setSelectionRange(0, 0);
        break;
      case 'end':
        element.setSelectionRange(len, len);
        break;
      default:
        element.setSelectionRange(0, len);
    }
  }
}
const Input = /*#__PURE__*/React.forwardRef((props, ref) => {
  var _suffix$props2;
  const {
    label,
    placeholder,
    type = 'outline',
    size = 'm',
    disabled,
    readonly,
    loading,
    error,
    active,
    prefix,
    suffix,
    autoFocus,
    onKeyDown,
    onBlue,
    onBlur,
    onFocus,
    htmlType,
    htmlName,
    autoComplete,
    width,
    prefixClass = 'zen-design',
    //TODO 写provider时统一改掉
    className,
    style,
    ...restProps
  } = props;
  const baseClassName = "".concat(prefixClass, "-input");
  useEffect(() => {
    errorConsoleLog(label !== undefined && placeholder !== undefined, 'Input', "'label' and 'placeholder' cannot be shared.");
  }, []);
  const [value, onChange] = useControllableValue(props, {
    defaultValue: undefined,
    defaultValuePropName: 'defaultValue',
    valuePropName: 'value',
    trigger: 'onChange'
  });
  const inputEl = useRef(null);
  const [shrink, setShrink] = useState(false);
  const [isFocus, setFocus] = useState(false);
  const canDoSomething = !disabled && !readonly && !loading;
  React.useImperativeHandle(ref, () => ({
    focus: option => {
      triggerFocus(inputEl.current, option);
    },
    blur: () => {
      var _inputEl$current;
      return (_inputEl$current = inputEl.current) === null || _inputEl$current === void 0 ? void 0 : _inputEl$current.blur();
    }
  }));
  useEffect(() => {
    if (isFocus) {
      setShrink(true);
    } else {
      if (value === undefined || value === '') {
        setShrink(false);
      } else {
        setShrink(true);
      }
    }
  }, [value, isFocus]);
  const prefixIcon = /*#__PURE__*/React.isValidElement(prefix) ? /*#__PURE__*/React.cloneElement(prefix, {
    className: classNames("".concat(baseClassName, "-prefixIcon"), prefix.props.className)
  }) : undefined;
  const suffixIcon = /*#__PURE__*/React.isValidElement(suffix) ? /*#__PURE__*/React.cloneElement(suffix, {
    className: classNames("".concat(baseClassName, "-suffixIcon"), suffix.props.className),
    onClick: () => {
      var _suffix$props, _suffix$props$onClick;
      (_suffix$props = suffix.props) === null || _suffix$props === void 0 || (_suffix$props$onClick = _suffix$props.onClick) === null || _suffix$props$onClick === void 0 || _suffix$props$onClick.call(_suffix$props, value);
    },
    style: !!((_suffix$props2 = suffix.props) !== null && _suffix$props2 !== void 0 && _suffix$props2.onClick) ? {
      cursor: 'pointer'
    } : {
      pointerEvents: 'none'
    }
  }) : undefined;
  const attrs = pickDomAttrs(restProps);
  const InputContent = /*#__PURE__*/_jsx("input", {
    ...attrs.data,
    autoFocus: autoFocus,
    ref: inputEl,
    type: htmlType,
    name: htmlName,
    autoComplete: autoComplete,
    className: classNames('content', {
      ["".concat(size)]: !!size,
      ["with-prefixIcon"]: !!prefixIcon,
      ["with-suffixIcon"]: !!suffixIcon,
      ['content-disabled']: disabled
    }),
    placeholder: label === undefined ? placeholder : undefined,
    value: value !== null && value !== void 0 ? value : '',
    onChange: e => {
      onChange(e.target.value);
    },
    disabled: !canDoSomething,
    onFocus: e => {
      setFocus(true);
      canDoSomething && shrink === false && setShrink(true);
      !!onFocus && onFocus(value, e);
    },
    onBlur: e => {
      setFocus(false);
      (value === undefined || value === '') && shrink === true && setShrink(false);
      !!onBlue && onBlue(value, e);
      !!onBlur && onBlur(value, e);
    },
    onKeyDown: e => !!onKeyDown && onKeyDown(value, e)
  });
  if (label === undefined) {
    return /*#__PURE__*/_jsx(Chips, {
      active: active,
      type: type,
      size: size,
      disabled: disabled,
      error: error,
      loading: loading,
      readonly: readonly,
      prefix: prefix,
      suffix: suffixIcon,
      style: style,
      width: width,
      noPadding: true,
      className: classNames(className, "".concat(baseClassName, "-forChips")),
      children: InputContent
    });
  }
  return /*#__PURE__*/_jsx(TextField, {
    label: label,
    type: type,
    shrink: shrink,
    isFocus: isFocus || !!active,
    size: size,
    disabled: disabled,
    error: error,
    loading: loading,
    readonly: readonly,
    prefix: prefix,
    suffix: suffixIcon,
    style: style,
    width: width,
    className: classNames(className, baseClassName),
    children: InputContent
  });
});
export default Input;